import React, { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const loadTurnstileScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Turnstile requires a browser environment.'));
      return;
    }

    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const handleLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile finished loading without initializing.'));
      }
    };

    const handleError = () => {
      reject(new Error('Turnstile failed to load.'));
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

const TurnstileWidget = ({
  siteKey,
  onTokenChange,
  onStatusChange,
  resetKey = 0,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({
    onStatusChange,
    onTokenChange,
  });
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    callbacksRef.current = {
      onStatusChange,
      onTokenChange,
    };
  }, [onStatusChange, onTokenChange]);

  useEffect(() => {
    callbacksRef.current.onTokenChange('');
    setLoadError('');

    if (!siteKey || !containerRef.current) {
      return undefined;
    }

    let isCancelled = false;

    const mountWidget = async () => {
      callbacksRef.current.onStatusChange?.('loading');

      try {
        const turnstile = await loadTurnstileScript();

        if (isCancelled || !containerRef.current) {
          return;
        }

        if (widgetIdRef.current !== null) {
          turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        containerRef.current.innerHTML = '';

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'quote_request',
          theme: 'light',
          size: 'flexible',
          callback: (token) => {
            callbacksRef.current.onTokenChange(token);
            callbacksRef.current.onStatusChange?.('ready');
          },
          'error-callback': () => {
            callbacksRef.current.onTokenChange('');
            callbacksRef.current.onStatusChange?.('error');
            setLoadError(
              'Spam protection could not be loaded. Please refresh and try again.'
            );
            return true;
          },
          'expired-callback': () => {
            callbacksRef.current.onTokenChange('');
            callbacksRef.current.onStatusChange?.('expired');
          },
          'timeout-callback': () => {
            callbacksRef.current.onTokenChange('');
            callbacksRef.current.onStatusChange?.('expired');
          },
        });

        callbacksRef.current.onStatusChange?.('pending');
      } catch (error) {
        if (isCancelled) {
          return;
        }

        callbacksRef.current.onStatusChange?.('error');
        setLoadError(
          error instanceof Error
            ? error.message
            : 'Spam protection could not be loaded. Please refresh and try again.'
        );
      }
    };

    mountWidget();

    return () => {
      isCancelled = true;

      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  useEffect(() => {
    if (!window.turnstile || widgetIdRef.current === null || resetKey === 0) {
      return;
    }

    window.turnstile.reset(widgetIdRef.current);
    callbacksRef.current.onTokenChange('');
    callbacksRef.current.onStatusChange?.('pending');
    setLoadError('');
  }, [resetKey]);

  return (
    <div className="space-y-2">
      <div ref={containerRef} />
      {loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : null}
    </div>
  );
};

export default TurnstileWidget;
